import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import axios from 'axios'
import { useChangePassword } from '@/hooks/learner/useChangePassword'
import { validateNewPassword } from '../lib/validateNewPassword'
import type { ApiResponse } from '@/api/client'
import type { ChangePasswordErrorCode } from '@/api/auth'

const SERVER_ERROR_TO_I18N: Record<ChangePasswordErrorCode, string> = {
  INVALID_CURRENT_PASSWORD: 'profile.password.error.invalidCurrent',
  WEAK_PASSWORD: 'profile.password.error.weak',
  SAME_AS_CURRENT_PASSWORD: 'profile.password.error.sameAsCurrent',
}

interface PasswordChangeFormProps {
  onSuccess?: () => void
}

export function PasswordChangeForm({ onSuccess }: PasswordChangeFormProps) {
  const { t } = useTranslation('learner')
  const mutation = useChangePassword()
  const currentRef = useRef<HTMLInputElement>(null)

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [clientError, setClientError] = useState<string | null>(null)

  const currentId = useId()
  const newId = useId()
  const confirmId = useId()
  const errorId = useId()

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = validateNewPassword({ current, next, confirm })
    if (!result.ok) {
      setClientError(`profile.password.error.${result.reason}`)
      return
    }
    setClientError(null)
    mutation.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          toast.success(t('profile.password.success'))
          setCurrent('')
          setNext('')
          setConfirm('')
          onSuccess?.()
        },
        onError: (err) => {
          let code: ChangePasswordErrorCode | null = null
          if (axios.isAxiosError(err)) {
            const data = err.response?.data as ApiResponse<unknown> | undefined
            const raw = data?.error
            if (raw && raw in SERVER_ERROR_TO_I18N) {
              code = raw as ChangePasswordErrorCode
            }
          }
          const key = code ? SERVER_ERROR_TO_I18N[code] : 'profile.password.error.unknown'
          toast.error(t(key))
        },
      },
    )
  }

  const disabled = mutation.isPending

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <div className="flex flex-col gap-3.5">
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={currentId}
          className="text-lc-v3-body-2 font-medium text-lc-text-secondary"
        >
          {t('profile.password.currentLabel')}
        </label>
        <input
          ref={currentRef}
          id={currentId}
          type="password"
          autoFocus
          autoComplete="current-password"
          placeholder={t('profile.password.currentPlaceholder')}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          aria-invalid={clientError === 'profile.password.error.currentRequired'}
          aria-describedby={clientError === 'profile.password.error.currentRequired' ? errorId : undefined}
          className="h-12 w-full rounded-lg border border-lc-disabled-border px-4 text-lc-v3-body-1 font-medium text-lc-text-primary outline-none placeholder:text-lc-text-disabled focus:border-lc-key-500 focus:ring-2 focus:ring-lc-key-100"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={newId}
          className="text-lc-v3-body-2 font-medium text-lc-text-secondary"
        >
          {t('profile.password.newLabel')}
        </label>
        <input
          id={newId}
          type="password"
          autoComplete="new-password"
          placeholder={t('profile.password.newPlaceholder')}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          aria-invalid={clientError === 'profile.password.error.lengthRange' || clientError === 'profile.password.error.sameAsCurrent'}
          aria-describedby={clientError === 'profile.password.error.lengthRange' || clientError === 'profile.password.error.sameAsCurrent' ? errorId : undefined}
          className="h-12 w-full rounded-lg border border-lc-disabled-border px-4 text-lc-v3-body-1 font-medium text-lc-text-primary outline-none placeholder:text-lc-text-disabled focus:border-lc-key-500 focus:ring-2 focus:ring-lc-key-100"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={confirmId}
          className="text-lc-v3-body-2 font-medium text-lc-text-secondary"
        >
          {t('profile.password.confirmLabel')}
        </label>
        <input
          id={confirmId}
          type="password"
          autoComplete="new-password"
          placeholder={t('profile.password.confirmPlaceholder')}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          aria-invalid={clientError === 'profile.password.error.confirmMismatch'}
          aria-describedby={clientError === 'profile.password.error.confirmMismatch' ? errorId : undefined}
          className="h-12 w-full rounded-lg border border-lc-disabled-border px-4 text-lc-v3-body-1 font-medium text-lc-text-primary outline-none placeholder:text-lc-text-disabled focus:border-lc-key-500 focus:ring-2 focus:ring-lc-key-100"
        />
      </div>

      {clientError && (
        <p id={errorId} role="alert" className="text-lc-v3-caption-1 font-semibold text-lc-text-error">
          {t(clientError)}
        </p>
      )}
      </div>

      <button
        type="submit"
        disabled={disabled}
        className="ml-auto flex h-12 items-center justify-center rounded-xl bg-lc-key-500 px-5 text-lc-v3-body-1 font-semibold text-lc-text-on-key hover:bg-lc-key-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500 disabled:cursor-wait disabled:bg-lc-disabled-bg disabled:text-lc-text-disabled"
      >
        {t('profile.password.submit')}
      </button>
    </form>
  )
}
